import { readdir, readFile, stat } from "node:fs/promises";
import { join, resolve, sep } from "node:path";
import { parseFrontmatter } from "@vortex-os/core";
import type {
  DecisionEntry,
  DecisionFilter,
  DecisionLogFrontmatter,
} from "./types.js";

const FILENAME_PATTERN = /^(\d{4}-\d{2}-\d{2})-(.+)\.md$/;

/**
 * Directory-backed Decision-Log store. Expected layout is flat —
 * `<rootDir>/YYYY-MM-DD-<slug>.md`, no year/month subdirectories.
 *
 * Non-entry files at the root (README, _TEMPLATE, anything not matching
 * the date-prefix pattern) are silently ignored.
 *
 * A missing root directory returns an empty list rather than throwing,
 * so a brand-new template clone with no entries is a normal state.
 */
export class DecisionStore {
  constructor(readonly rootDir: string) {}

  /** All entries, sorted by date ascending, then slug ascending. */
  async list(): Promise<readonly DecisionEntry[]> {
    let names: string[];
    try {
      names = await readdir(this.rootDir);
    } catch (e) {
      if ((e as NodeJS.ErrnoException).code === "ENOENT") return [];
      throw e;
    }
    const out: DecisionEntry[] = [];
    for (const name of names) {
      const match = name.match(FILENAME_PATTERN);
      if (!match) continue;
      const path = join(this.rootDir, name);
      try {
        const info = await stat(path);
        if (!info.isFile()) continue;
      } catch {
        continue;
      }
      const raw = await readFile(path, "utf8");
      const { frontmatter, body } =
        parseFrontmatter<DecisionLogFrontmatter>(raw);
      const date = match[1] ?? "";
      const slug = match[2] ?? "";
      out.push({ date, slug, path, frontmatter, body });
    }
    return out.sort((a, b) =>
      a.date === b.date
        ? a.slug.localeCompare(b.slug)
        : a.date.localeCompare(b.date),
    );
  }

  /**
   * Entries whose filename starts with `date` (`YYYY-MM-DD`). Multiple
   * decisions on the same day are returned in slug-ascending order.
   */
  async getByDate(date: string): Promise<readonly DecisionEntry[]> {
    const all = await this.list();
    return all.filter((e) => e.date === date);
  }

  /**
   * The first entry matching `date` and (optionally) `slug`. When no slug is
   * provided and multiple entries share the date, the lexicographically-first
   * one is returned.
   */
  async get(date: string, slug?: string): Promise<DecisionEntry | undefined> {
    const matches = await this.getByDate(date);
    if (slug === undefined) return matches[0];
    return matches.find((e) => e.slug === slug);
  }

  /** Most recent entry by date (descending), then slug (descending). */
  async getLatest(): Promise<DecisionEntry | undefined> {
    const all = await this.list();
    if (all.length === 0) return undefined;
    return all[all.length - 1];
  }

  /**
   * Subset of entries matching the given criteria. Empty/undefined fields
   * are ignored. Returns entries in the same order as {@link list}.
   */
  async filter(criteria: DecisionFilter): Promise<readonly DecisionEntry[]> {
    const all = await this.list();
    return all.filter((e) => {
      if (criteria.status !== undefined && e.frontmatter.status !== criteria.status) {
        return false;
      }
      if (criteria.tag !== undefined) {
        const tags = e.frontmatter.tags ?? [];
        if (!tags.includes(criteria.tag)) return false;
      }
      if (criteria.fromDate !== undefined && e.date < criteria.fromDate) {
        return false;
      }
      if (criteria.toDate !== undefined && e.date > criteria.toDate) {
        return false;
      }
      return true;
    });
  }

  /** Resolve the file path for a given (date, slug), without creating it. */
  pathFor(date: string, slug: string): string {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new Error(`Invalid date: ${date} (expected YYYY-MM-DD)`);
    }
    validateSegment("slug", slug);
    const abs = join(this.rootDir, `${date}-${slug}.md`);
    assertContained(abs, this.rootDir);
    return abs;
  }
}

/**
 * Reject a filename segment (slug / keyword) that could escape the store
 * directory or smuggle a path into the filename. `slug` is operator/model
 * supplied, so the same defensive rules as
 * `memory-extended/src/mcp/document-tools.ts` apply: no path separators, no
 * `..` traversal, no absolute paths / drive letters, no NUL / control chars.
 */
function validateSegment(label: string, value: string): void {
  const v = (value ?? "").trim();
  if (v.length === 0) throw new Error(`${label} is required`);
  if (v.includes("/") || v.includes("\\"))
    throw new Error(`${label} must not contain path separators: ${value}`);
  if (/(^|[\\/])\.\.([\\/]|$)/.test(v) || v === "..")
    throw new Error(`${label} must not contain '..': ${value}`);
  if (v.startsWith("/") || v.startsWith("\\") || /^[a-zA-Z]:/.test(v))
    throw new Error(`${label} must be a relative name, not an absolute path: ${value}`);
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(v))
    throw new Error(`${label} must not contain NUL or control characters: ${value}`);
}

/**
 * Defense-in-depth containment: the resolved absolute target must live inside
 * `rootDir`. Catches any traversal the segment check missed (symlinks,
 * Unicode tricks, future callers). Throws on escape.
 */
function assertContained(abs: string, rootDir: string): void {
  const root = resolve(rootDir);
  const target = resolve(abs);
  if (target !== root && !(target + sep).startsWith(root + sep)) {
    throw new Error(`Refusing to write outside the store directory: ${abs}`);
  }
}
